import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import ResponsiveModal from "@components/ResponsiveModal";
import ResponsiveTable from "@components/ResponsiveTable";
import { formatWorkorderRef } from "@/utils/entity-ref";

/**
 * 删除设备前的工单勾选弹窗。
 *
 * 列出「删完本次设备后会失去全部关联设备」的工单，默认全部不勾选，
 * 由用户决定把其中哪些一起软删：设备一定会删，只有勾中的工单会跟着删，
 * 没勾的保留（会变成无设备工单）。默认全不选是刻意设计——避免误点主按钮
 * 连带删掉一批工单。PC 走 Modal，H5 走底部 Drawer
 * （ResponsiveModal / ResponsiveTable 内部按 state.App.isPC 切换）。
 */
const DeleteWorkordersModal = ({
  open,
  workorders = [],
  canDeleteWorkorders = true,
  confirmLoading = false,
  onCancel,
  onOk,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // 每次打开都重置为全不选（需求：默认不全选）
  useEffect(() => {
    if (open) {
      setSelectedRowKeys([]);
    }
  }, [open]);

  const columns = useMemo(
    () => [
      {
        title: "Work Order",
        dataIndex: "workordernumber",
        key: "workordernumber",
        width: 140,
        render: (value, record) => formatWorkorderRef(value || record.id),
      },
      {
        title: "Issue",
        dataIndex: "issue",
        key: "issue",
        ellipsis: true,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 130,
      },
      {
        title: "Priority",
        dataIndex: "priority",
        key: "priority",
        width: 110,
      },
      {
        title: "Due Date",
        dataIndex: "due_date",
        key: "due_date",
        width: 120,
        render: (value) => (value ? moment(value).format("YYYY-MM-DD") : "-"),
      },
    ],
    []
  );

  const selectedCount = selectedRowKeys.length;
  const okText =
    selectedCount > 0
      ? `Delete Equipment + ${selectedCount} Work Order${selectedCount > 1 ? "s" : ""}`
      : "Delete Equipment";

  return (
    <ResponsiveModal
      open={open}
      title="Work Orders Losing All Equipment"
      width={860}
      height="75vh"
      okText={okText}
      cancelText="Cancel"
      okButtonProps={{ danger: true }}
      confirmLoading={confirmLoading}
      onOk={() => onOk(canDeleteWorkorders ? selectedRowKeys : [])}
      onCancel={onCancel}
    >
      <p style={{ marginTop: 0, color: "#595959" }}>
        Deleting the selected equipment will leave the following work orders without
        any equipment. Select the ones you also want to delete — unselected work
        orders are kept, and Cancel deletes nothing.
      </p>

      <div style={{ maxHeight: "45vh", overflow: "auto" }}>
        <ResponsiveTable
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={workorders}
          pagination={false}
          showToolbar={false}
          scroll={{ y: 320 }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys || []),
            getCheckboxProps: () => ({ disabled: !canDeleteWorkorders }),
          }}
          locale={{ emptyText: "No work orders" }}
        />
      </div>

      <div style={{ marginTop: 8, color: "#8c8c8c" }}>
        {selectedCount} of {workorders.length} selected — the equipment is always
        deleted, only the selected work orders are deleted with it.
      </div>
    </ResponsiveModal>
  );
};

export default DeleteWorkordersModal;
